import { useEffect, useRef } from 'react';
import { useSwitchAccess } from './context/SwitchAccessContextProvider';
import Logger from '../../utils/Logger';
import ObjectUtils from '../../utils/ObjectUtils';
import LessonPlayActivityUtils from '../lessonplay/activities/utils/LessonPlayActivityUtils';
// import { useLessonPlay } from '../lessonplay/context/LessonPlayContextProvider';

const KEY_SELECT = 13; // Enter key
const KEY_SCAN = 32; // Space bar key
const KEY_SCAN_TAB = 9; // Tab key
const KEY_SCANS = [KEY_SCAN, KEY_SCAN_TAB];

const CLASS_SWITCH_SELECTED_COMPONENT = 'switch-selected-component';

const createElementList = (elementsObject) => {
  const elements = [];

  const chatButtonButtonMode = document.querySelector('.chat-button-button-mode');
  if (chatButtonButtonMode) {
    elements.push(chatButtonButtonMode);
  }

  const updateReviewStatusAction = document.querySelector('.update-review-status-action');
  if (updateReviewStatusAction) {
    elements.push(updateReviewStatusAction);
  }

  const viewStudentAnswersAction = document.querySelector('.view-answer-action');
  if (viewStudentAnswersAction) {
    elements.push(viewStudentAnswersAction);
  }

  if (elementsObject.topBar && elementsObject.topBar.length > 0) {
    elements.push(...elementsObject.topBar);
  }

  if (elementsObject.body && elementsObject.body.length > 0) {
    elements.push(...elementsObject.body);
  }

  if (elementsObject.bottomBar && elementsObject.bottomBar.length > 0) {
    elements.push(...elementsObject.bottomBar);
  }

  // const chatButton = document.querySelector('.chat-button');
  // if (chatButton) {
  //   elements.push(chatButton);
  // }

  return elements;
};

const removeSelectedElementClass = () => {
  const currentElement = document.querySelectorAll(`.${CLASS_SWITCH_SELECTED_COMPONENT}`);
  if (currentElement && currentElement.length > 0) {
    currentElement.forEach((e) => {
      e.classList.remove(CLASS_SWITCH_SELECTED_COMPONENT);
    });
  }
};

const addClassToTheSelectedElement = (element) => {
  removeSelectedElementClass();
  if (element) {
    element.classList.add(CLASS_SWITCH_SELECTED_COMPONENT);
  }
};

const focusSelectedElement = (element) => {
  if (!element || !element.focus) {
    return;
  }
  const autoFocusInput = element.querySelector('.MuiSelect-select')
    || element.querySelector('[data-access-auto-focus="true"] input');
  if (autoFocusInput && autoFocusInput.focus) {
    autoFocusInput.focus();
    return;
  }
  element.focus();
};

export const useRegisterSwitchKeys = (switchAccessibility) => {
  const { switchAccessDomain, switchAccessElementDomain } = useSwitchAccess();
  const { hasDialogShown } = switchAccessDomain.domainData;
  const { elementsForSwitch } = switchAccessElementDomain.domainData;

  const currentSelectedIndex = useRef(-1);
  const continueFromIndex = useRef(-1);
  useEffect(() => {
    // if (!switchAccessibility) {
    //   return undefined;
    // }

    let keyDownEventListener = null;

    if (hasDialogShown) {
      const elements = createElementList(elementsForSwitch);
      if (!elements || elements.length === 0) {
        Logger.logWhenDebugModeIsOn('No elements to register the event');
        return undefined;
      }

      Logger.logWhenDebugModeIsOn('Register switch access');
      currentSelectedIndex.current = -1;
      removeSelectedElementClass();
      if (!switchAccessibility && document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }

      keyDownEventListener = (e) => {
        if (switchAccessibility) {
          e.preventDefault();
        }
        if (e.which === KEY_SCAN_TAB && e.shiftKey) {
          if (switchAccessibility) {
            return;
          }
          LessonPlayActivityUtils.stopMedias();
          // Shift+Tab: Go to previous element
          const instructionDialog = document.querySelector('[data-test="activity-instruction-dialog"]');
          const dialog = document.querySelector('.free-text-response-popup-dialog');
          const chatDialog = document.querySelector('.copilot-popup-container');
          const chatButtonButtonMode = document.querySelector('.chat-panel')
            || document.querySelector('.update-review-status-action-dialog')
            || document.querySelector('.score-answer-form-answer-paper-container')
            || document.querySelector('.copy-lesson-page-action-dialog')
            || document.querySelector('.copy-lesson-multiple-pages-action-dialog')
            || document.querySelector('.fill-in-the-blank-activity');

          if ((instructionDialog || dialog || chatDialog || chatButtonButtonMode) && !switchAccessibility) {
            // Let dialogs/forms rely on native browser tab order.
            return;
          }
          e.preventDefault();

          let newIndex = currentSelectedIndex.current;

          if (currentSelectedIndex.current < 0) {
            // Start from the last element if nothing is selected
            newIndex = elements.length - 1;
          } else {
            const currentElement = elements[currentSelectedIndex.current];
            if (currentElement) {
              currentElement.dispatchEvent(new CustomEvent('switchAccessLeaveElement', {
                isSwitchAccess: true,
              }));
            }

            newIndex = currentSelectedIndex.current - 1;
          }
          if (newIndex < 0) {
            newIndex = elements.length - 1;
          }
          currentSelectedIndex.current = newIndex;

          const element = elements[currentSelectedIndex.current];
          focusSelectedElement(element);
          addClassToTheSelectedElement(element);

          let el = element.querySelector('[data-switch-access-scan=\'true\']');
          if (element.dataset.switchAccessScan === 'true') {
            el = element;
          }
          if (el) {
            el.dispatchEvent(new CustomEvent('switchAccessScan', {
              detail: {
                isSwitchAccess: true,
                triggeredElement: element,
              },
            }));
          }
        } else if (KEY_SCANS.includes(e.which)) {
          const nextElement = () => {
            e.preventDefault();
            LessonPlayActivityUtils.stopMedias();
            let newIndex = currentSelectedIndex.current;

            if (currentSelectedIndex.current < 0) {
              if (continueFromIndex.current >= 0) {
                newIndex = continueFromIndex.current;
                continueFromIndex.current = -1;
              } else {
                newIndex = elementsForSwitch.topBar && switchAccessibility ? elementsForSwitch.topBar.length : 0;
              }
            } else {
              const currentElement = elements[currentSelectedIndex.current];
              if (currentElement) {
                currentElement.dispatchEvent(new CustomEvent('switchAccessLeaveElement', {
                  isSwitchAccess: true,
                }));
              }

              newIndex = currentSelectedIndex.current + 1;
            }
            if (newIndex >= elements.length) {
              newIndex = 0;
            }
            currentSelectedIndex.current = newIndex;

            const element = elements[currentSelectedIndex.current];
            if (!switchAccessibility && e.which === KEY_SCAN_TAB) {
              focusSelectedElement(element);
            }

            setTimeout(() => {
              addClassToTheSelectedElement(element);
            });

            let el = element.querySelector('[data-switch-access-scan=\'true\']');
            if (element.dataset.switchAccessScan === 'true') {
              el = element;
            }
            if (el) {
              el.dispatchEvent(new CustomEvent('switchAccessScan', {
                detail: {
                  isSwitchAccess: true,
                  triggeredElement: element,
                },
              }));
            }
          };

          const videoDialog = document.querySelector('.video-player-dialog-content');
          if (videoDialog && !switchAccessibility) {
            return;
          }

          const freeTextResponseDialog = document.querySelector('.free-text-response-popup-dialog');
          if (freeTextResponseDialog && !switchAccessibility) {
            const last = e.target.matches('[data-test="custom-dialog-actions-1"]');
            if (last) {
              const focusableElements = freeTextResponseDialog.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
              );
              if (focusableElements.length > 0) {
                focusableElements[0].focus();
              }
            }
            return;
          }

          const instructionDialog = document.querySelector('[data-test="activity-instruction-dialog"]');
          if (instructionDialog && !switchAccessibility) {
            return;
          }

          const chatDialog = document.querySelector('.copilot-popup-container');
          if (chatDialog && !switchAccessibility) {
            if (e.target.classList.contains('exit-button')) {
              chatDialog.focus();
            }
            return;
          }

          const chatButtonButtonMode = document.querySelector('.chat-panel')
          || document.querySelector('.update-review-status-action-dialog')
          || document.querySelector('.score-answer-form-answer-paper-container')
          || document.querySelector('.copy-lesson-page-action-dialog')
          || document.querySelector('.copy-lesson-multiple-pages-action-dialog')
          || document.querySelector('.fill-in-the-blank-activity');
          if (chatButtonButtonMode && !switchAccessibility) {
            return;
          }

          const currentSelectedElement = document.querySelector(`.${CLASS_SWITCH_SELECTED_COMPONENT}`);
          const isVideoElement = currentSelectedElement && currentSelectedElement.tagName && currentSelectedElement.tagName.toLowerCase() === 'video';
          if (isVideoElement && !switchAccessibility) {
            if (currentSelectedElement) {
              currentSelectedElement.focus();
              currentSelectedElement.addEventListener('focusout', () => {
                removeSelectedElementClass();
                nextElement();
              }, { once: true });
            }
            // For video element, space bar should play/pause the video instead of moving to the next element
            return;
          }
          nextElement();
        } else if (KEY_SELECT === e.which) {
          e.preventDefault();
          const videoDialog = document.querySelector('.video-player-dialog-content')
          || document.querySelector('.free-text-response-popup-dialog')
          || document.querySelector('.copilot-popup-container')
          || document.querySelector('[data-test="activity-instruction-dialog"]')
          || document.querySelector('.fill-in-the-blank-activity');
          if (videoDialog && !switchAccessibility) {
            return;
          }

          const element = elements[currentSelectedIndex.current];
          if (element) {
            if (!switchAccessibility && element.blur) {
              element.blur();
              ObjectUtils.setTimeout(() => {
                if (element.blur) {
                  element.blur();
                }
              }, 0);
            }
            if (element.dataset && element.dataset.skipSwitchAccessClick === 'true') {
              element.dispatchEvent(new CustomEvent('switchAccessSelect', {
                isSwitchAccess: true,
              }));
            } else {
              element.click();
            }
          }
        }
      };
    } else {
      Logger.logWhenDebugModeIsOn('register spacebar to close the dialog');
      keyDownEventListener = (e) => {
        if (KEY_SCANS.includes(e.which)) {
          switchAccessDomain.updateShowDialog(false, true);

          ObjectUtils.setTimeout(() => {
            window.dispatchEvent(new KeyboardEvent('keydown', { keyCode: KEY_SCAN }));
          }, 100);
        } else if (switchAccessibility) {
          e.preventDefault();
        }
      };
    }

    if (keyDownEventListener) {
      window.addEventListener('keydown', keyDownEventListener);
    }

    return () => {
      Logger.logWhenDebugModeIsOn('unregister enter and spacebar key');
      if (keyDownEventListener) {
        window.removeEventListener('keydown', keyDownEventListener);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDialogShown, elementsForSwitch]);

  const selectElementByIndex = (index) => {
    if (index < 0) {
      return;
    }
    const { elementsForSwitch: elementsForSwitch2 } = switchAccessElementDomain.domainData;
    const elements = createElementList(elementsForSwitch2);
    const element = elements[index];
    currentSelectedIndex.current = index;
    addClassToTheSelectedElement(element);
  };

  const setContinueFromIndex = (index) => {
    if (index < 0) {
      return;
    }
    continueFromIndex.current = index;
  };

  return {
    currentSelectedIndex,
    selectElementByIndex,
    setContinueFromIndex,
  };
  // return {
  //   currentSelectedIndex: switchAccessibility ? currentSelectedIndex : null,
  //   selectElementByIndex: switchAccessibility ? selectElementByIndex : () => { },
  //   setContinueFromIndex: switchAccessibility ? setContinueFromIndex : () => { },
  // };
};

export const useShowSwitchAccessDialog = (switchAccessibility, hasInstruction, showDialogDelay = 4000) => {
  const { switchAccessDomain } = useSwitchAccess();

  const showSwitchAccessDialog = (delay = 500) => {
    if (!switchAccessibility) {
      return true;
    }

    ObjectUtils.setTimeout(() => {
      const { hasDialogShown } = switchAccessDomain.domainData;
      if (!hasDialogShown) {
        switchAccessDomain.updateShowDialog(true);
      }
    }, delay);
    return switchAccessDomain.domainData.hasDialogShown;
  };

  useEffect(() => {
    if (switchAccessDomain.domainData.isDialogShowing) {
      // Ensure it starts without showing the dialog
      switchAccessDomain.updateShowDialog(false, switchAccessDomain.domainData.hasDialogShown);
    }

    if (!hasInstruction && switchAccessibility) {
      showSwitchAccessDialog(showDialogDelay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    showSwitchAccessDialog,
  };
};

export const useUpdateElementsToScan = () => {
  const { switchAccessElementDomain } = useSwitchAccess();
  const previousTabTargetElementsRef = useRef([]);

  const clearTabIndexTargets = (element) => {
    if (!element || !element.removeAttribute) {
      return;
    }
    element.removeAttribute('tabindex');
    const videoElements = element.querySelectorAll('video');
    videoElements.forEach((video) => {
      if (video && video.removeAttribute) {
        video.removeAttribute('tabindex');
      }
    });
  };

  const updateElementsToScan = async (elements) => {
    previousTabTargetElementsRef.current.forEach(clearTabIndexTargets);

    const elementsList = createElementList(elements);
    previousTabTargetElementsRef.current = elementsList.filter(Boolean);

    elementsList.forEach((element) => {
      if (element && element.setAttribute) {
        element.setAttribute('tabindex', '1');

        const videoElements = element.querySelectorAll('video');
        videoElements.forEach((video) => {
          if (video && video.setAttribute) {
            video.setAttribute('tabindex', '1');
          }
        });
      }
    });
    await switchAccessElementDomain.updateElementToScan(elements);
  };

  return {
    updateElementsToScan,
  };
};

export const useRegisterEvent = () => {
  // const { lessonPlayDomain } = useLessonPlay();
  // const { userProfile } = lessonPlayDomain.domainData;
  // const { switchAccessibility } = userProfile;

  const registerSwitchEvent = (eventName, elementRef, func) => {
    // if (!switchAccessibility || switchAccessibility === 0) {
    //   return () => { };
    // }

    const handleSwitchAccessScan = (params) => {
      func(params);
    };

    if (elementRef.current) {
      elementRef.current.addEventListener(eventName, handleSwitchAccessScan);
    }

    return () => {
      if (elementRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        elementRef.current.removeEventListener(eventName, handleSwitchAccessScan);
      }
    };
  };

  return {
    registerSwitchEvent,
  };
};

export const useRegisterSwitchAccessScanEvent = (elementRef, func = () => { }) => {
  const {
    registerSwitchEvent,
  } = useRegisterEvent();

  const cleanupFunction = useRef(null);
  useEffect(() => {
    cleanupFunction.current = registerSwitchEvent('switchAccessScan', elementRef, func);

    return () => {
      cleanupFunction.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef.current]);

  return cleanupFunction.current;
};

export const useRegisterSwitchAccessSelectEvent = (elementRef, func = () => { }) => {
  const {
    registerSwitchEvent,
  } = useRegisterEvent();

  useEffect(() => {
    const cleanupFunction = registerSwitchEvent('switchAccessSelect', elementRef, func);

    return () => {
      cleanupFunction();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef.current]);
};
